import { useEffect, useRef, useState, type ReactElement } from 'react'
import { RouteDetailsTestTags } from './testTags'
import type { RouteDetailsStringSet } from './strings'
import type { MapCoordinate } from './sections'
import { loadMapKit } from './loadMapKit'
import { useMapKitToken } from './useMapKitToken'
import type { MapKitMap } from './mapkit'
import { resolveMapDark } from './resolveMapDark'
import styles from './MapRow.module.css'

/** Same span as the station panel's map — a couple of streets around the pin. */
const REGION_SPAN_DEG = 0.004

/** `--mm-color-accent-blue` literal — MapKit JS cannot read CSS vars. */
const ACCENT_BLUE = '#0a84ff'

export interface DestinationMapRowProps {
  readonly stationName: string
  readonly destination: MapCoordinate
  readonly strings: RouteDetailsStringSet
}

/**
 * A ride route's map: a live Apple map centred on the destination station
 * with one pin — iOS `MapRowView` without an origin. Web used to draw a
 * static grid here for every route with a ride. The grid and its pin stay
 * on screen until the map is built, and when MapKit fails (fail-safe: never
 * throws into the host). The Playwright harness never mounts it — `MapRow`
 * keeps the static row there.
 */
export function DestinationMapRow({
  stationName,
  destination,
  strings,
}: DestinationMapRowProps): ReactElement {
  const mapDivRef = useRef<HTMLDivElement | null>(null)
  const tokenEntry = useMapKitToken()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!tokenEntry || !mapDivRef.current) return
    let cancelled = false
    let map: MapKitMap | null = null
    loadMapKit()
      .then((mapkit) => {
        if (cancelled || !mapDivRef.current) return
        if (!mapkit.initialized) {
          mapkit.init({ authorizationCallback: (done) => done(tokenEntry.token) })
        }
        map = new mapkit.Map(mapDivRef.current, {
          isScrollEnabled: false,
          isZoomEnabled: false,
          isRotationEnabled: false,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          colorScheme: resolveMapDark() ? 'dark' : 'light',
        })
        const coordinate = new mapkit.Coordinate(destination.latitude, destination.longitude)
        map.region = new mapkit.CoordinateRegion(
          coordinate,
          new mapkit.CoordinateSpan(REGION_SPAN_DEG, REGION_SPAN_DEG),
        )
        map.addAnnotation(new mapkit.MarkerAnnotation(coordinate, { color: ACCENT_BLUE }))
        setReady(true)
      })
      .catch(() => {
        // Stay on the placeholder when MapKit JS fails to load.
      })
    return (): void => {
      cancelled = true
      if (map) map.destroy()
      setReady(false)
    }
  }, [tokenEntry, destination.latitude, destination.longitude])

  return (
    <div
      className={styles.map}
      data-testid={RouteDetailsTestTags.map}
      role="img"
      aria-label={strings.mapOf(stationName)}
    >
      <div
        ref={mapDivRef}
        className={styles.canvas}
        data-testid="rd-map-canvas"
        aria-hidden="true"
      />
      {!ready && (
        <span className={styles.pin} data-testid="rd-map-pin">
          <span className={styles.pinDot} />
        </span>
      )}
    </div>
  )
}
